import React from 'react';
import { Link } from 'react-router-dom';
import { MainNavigation } from '../main/MainNavigation';
import { 
  ChefHat,
  Calendar,
  ShoppingCart,
  Clock,
  Users,
  Heart,
  DollarSign,
  Utensils,
  Target,
  Zap,
  Star,
  Shield,
  Smartphone,
  Globe,
  TrendingUp,
  X,
  Check,
  Plus,
  Minus,
  Play,
  Bookmark,
  Timer,
  Sparkles,
  ArrowDown
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

export function LandingPage() {
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const redirectToApp = useAuthStore((state) => state.redirectToApp);

  const handleSignupRedirect = (e: React.MouseEvent) => {
    e.preventDefault();
    redirectToApp('/signup');
  };

  const handleAppRedirect = (e: React.MouseEvent) => {
    e.preventDefault();
    redirectToApp('/');
  };

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const ctaButton = (text: string, className: string) => {
    if (isAuthenticated) {
      return (
        <a 
          href="https://app.menioo.com" 
          onClick={handleAppRedirect}
          className={className}
        >
          {text}
        </a>
      );
    }
    return (
      <a 
        href="https://app.menioo.com/signup"
        onClick={handleSignupRedirect}
        className={className}
      >
        {text}
      </a>
    );
  };

  return (
    <div className="min-h-screen bg-menioo-cream">
      <MainNavigation />

      {/* ═══════════════════════════════════════════════════════════
          1. HERO SECTION
          Positionnement : Du scroll passif à la cuisine active
      ═══════════════════════════════════════════════════════════ */}
      <section className="relative bg-gradient-to-br from-menioo-cream via-white to-menioo-cream py-16 md:py-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-menioo-green/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-menioo-orange/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center bg-menioo-green/10 text-menioo-green-dark px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="h-4 w-4 mr-2" />
                Des milliers de recettes à découvrir et à cuisiner pour de vrai
              </div>
              
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-menioo-navy leading-tight">
                  Cuisinez ce que{' '}
                  <span className="text-menioo-green">vous aimez vraiment.</span>
                </h1>
                <p className="text-lg md:text-xl text-gray-700 max-w-lg">
                  Explorez les recettes de la communauté, importez celles de vos créateurs préférés sur TikTok et Instagram, planifiez votre semaine et <strong>passez enfin à l'action</strong>.
                </p>
              </div>
              
              <div className="flex items-center space-x-8 text-sm text-gray-600">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-menioo-green/10 flex items-center justify-center mr-2">
                    <Clock className="h-4 w-4 text-menioo-green" />
                  </div>
                  <span>2 min pour planifier</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-menioo-orange/10 flex items-center justify-center mr-2">
                    <DollarSign className="h-4 w-4 text-menioo-orange" />
                  </div>
                  <span>-40% de dépenses</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-menioo-green/10 flex items-center justify-center mr-2">
                    <Heart className="h-4 w-4 text-menioo-green" />
                  </div>
                  <span>0 gaspillage</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {ctaButton(
                  'Commencer gratuitement',
                  'inline-flex items-center justify-center bg-menioo-green text-white px-8 py-4 rounded-xl text-center font-semibold text-lg hover:bg-menioo-green-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                )}
                
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="inline-flex items-center justify-center bg-white text-menioo-navy border-2 border-gray-200 px-8 py-4 rounded-xl text-center font-semibold text-lg hover:border-menioo-green transition-all group"
                >
                  Voir comment ça marche
                  <ArrowDown className="h-5 w-5 ml-2 group-hover:translate-y-0.5 transition-transform" />
                </button>
              </div>
              
              <p className="text-sm text-gray-500 flex items-center">
                <Shield className="h-4 w-4 text-menioo-green mr-2" />
                Gratuit, sans carte bancaire. Connexion Google sécurisée.
              </p>
            </div>
            
            <div className="hidden lg:block relative">
              <div className="absolute -top-6 -left-6 w-28 h-28 bg-menioo-orange/15 rounded-full animate-bounce-subtle"></div>
              <div className="absolute -bottom-10 -right-10 w-36 h-36 bg-menioo-green/15 rounded-full animate-bounce-subtle" style={{ animationDelay: '1s' }}></div>
              
              <div className="relative bg-white rounded-2xl shadow-2xl p-4 max-w-md mx-auto">
                <img 
                  src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80" 
                  alt="Cuisiner avec Menioo - recettes communauté et influenceurs" 
                  className="rounded-xl w-full"
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-menioo-green rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Communaute</p>
                    <p className="text-sm font-semibold text-menioo-navy">+1000 recettes</p>
                  </div>
                </div>
                <div className="absolute -top-3 -right-3 bg-white rounded-xl shadow-lg p-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-menioo-orange rounded-full flex items-center justify-center">
                    <ShoppingCart className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Liste générée</p>
                    <p className="text-sm font-semibold text-menioo-navy">12 ingrédients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          2. LE PROBLEME - PAS Framework
          Agiter la douleur du scroll passif
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Ca vous parle ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              L'inspiration ne manque pas — ce qui manque, c'est un système pour passer à l'action.
            </p>
          </div>
          
          <div className="bg-menioo-navy/5 rounded-2xl p-8 max-w-3xl mx-auto mb-12">
            <p className="text-center text-menioo-navy leading-relaxed">
              <strong>Le secret ?</strong> Utilisez votre <strong>système 2</strong> (réflexion) <em>une seule fois par semaine</em> pour planifier vos repas. Puis laissez votre <strong>système 1</strong> (pilote automatique) prendre le relais au quotidien. Fini la fatigue décisionnelle du "qu'est-ce qu'on mange ce soir ?".
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border border-red-100">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mr-4">
                  <Smartphone className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-menioo-navy">Sans organisation</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Des <strong>dizaines de recettes sauvegardées</strong> sur TikTok, Insta ou Pinterest — jamais cuisinées</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">À 18h, c'est toujours le même refrain : <strong>"On mange quoi ?"</strong></span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700"><strong>Courses improvisées</strong> : oublis, doublons, ultra-transformés</span>
                </li>
                <li className="flex items-start">
                  <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Resultat : <strong>livraison, gaspillage, budget qui explose</strong></span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mr-4">
                  <ChefHat className="h-7 w-7 text-menioo-green" />
                </div>
                <h3 className="text-xl font-bold text-menioo-navy">Avec Menioo</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-menioo-green mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Vous <strong>découvrez de nouvelles recettes</strong> dans la communauté ou importez vos coups de cœur</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-menioo-green mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Votre <strong>semaine est planifiee</strong> en quelques clics</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-menioo-green mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">La <strong>liste de courses est prête</strong>, rien n'est oublié</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-menioo-green mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Ce soir, <strong>vous cuisinez pour de vrai</strong> — et vous etes fier</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <p className="inline-flex items-center bg-menioo-navy/5 rounded-full px-6 py-3 text-menioo-navy font-medium">
              <TrendingUp className="h-5 w-5 text-menioo-green mr-2" />
              Les utilisateurs Menioo cuisinent en moyenne <strong className="ml-1">4x plus de recettes</strong> qu'avant
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          3. PROPOSITION DE VALEUR
          Le pont entre inspiration et action
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-menioo-cream/30 to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Le pont entre <span className="text-menioo-orange">l'inspiration</span> et <span className="text-menioo-green">votre assiette</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Recettes de la communauté, trouvailles de vos influenceurs préférés ou vos propres créations — Menioo vous aide à tout cuisiner pour de vrai.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-green/10 to-menioo-green/20 flex items-center justify-center mx-auto mb-5">
                <Target className="h-8 w-8 text-menioo-green" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Atteignez vos objectifs</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Veggie, moins de viande, plus de légumes, budget maîtrisé. Vos envies alimentaires enfin respectées.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-orange/10 to-menioo-orange/20 flex items-center justify-center mx-auto mb-5">
                <DollarSign className="h-8 w-8 text-menioo-orange" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Economisez vraiment</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Fini les restos par défaut et les livraisons impulsives. Liste optimisée, zéro gaspillage.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-green/10 to-menioo-green/20 flex items-center justify-center mx-auto mb-5">
                <Heart className="h-8 w-8 text-menioo-green" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Mangez sainement</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Moins d'ultra-transformés, portions équilibrées, ingrédients de saison et circuits courts. Cuisinez ce qui est bon pour vous.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-menioo-orange/10 to-menioo-orange/20 flex items-center justify-center mx-auto mb-5">
                <Utensils className="h-8 w-8 text-menioo-orange" />
              </div>
              <h3 className="text-lg font-bold text-menioo-navy mb-3">Reprenez plaisir</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                La fierté de faire soi-même. Cuisinez les recettes de la communauté ou de vos créateurs préférés et épatez vos proches.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          4. COMMENT CA MARCHE
          Le parcours de l'utilisateur en 4 etapes
      ═══════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Du scroll à l'assiette en 4 etapes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Aussi simple que de liker une video. Aussi satisfaisant que de manger un plat fait maison.
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="space-y-16">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center mb-5">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-menioo-green to-menioo-green-dark flex items-center justify-center text-white text-2xl font-bold mr-4 shadow-lg">
                      1
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-menioo-navy">Trouvez vos recettes</h3>
                      <p className="text-sm text-menioo-green font-medium">La fin du "qu'est-ce qu'on mange ?"</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Parcourez les milliers de recettes partagées par la <strong>communauté Menioo</strong>, créez les vôtres, ou importez celles de vos créateurs préférés (TikTok, Instagram, blogs). Tout est au même endroit, prêt à être cuisiné.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center bg-menioo-cream px-3 py-1.5 rounded-full text-xs font-medium text-menioo-navy">
                      <Users className="h-3 w-3 mr-1 text-menioo-green" /> Communaute Menioo
                    </span>
                    <span className="inline-flex items-center bg-menioo-cream px-3 py-1.5 rounded-full text-xs font-medium text-menioo-navy">
                      <ChefHat className="h-3 w-3 mr-1 text-menioo-green" /> Vos creations
                    </span>
                    <span className="inline-flex items-center bg-menioo-cream px-3 py-1.5 rounded-full text-xs font-medium text-menioo-navy">
                      <Play className="h-3 w-3 mr-1 text-menioo-orange" /> TikTok & Instagram
                    </span>
                    <span className="inline-flex items-center bg-menioo-cream px-3 py-1.5 rounded-full text-xs font-medium text-menioo-navy">
                      <Globe className="h-3 w-3 mr-1 text-menioo-orange" /> Blogs
                    </span>
                  </div>
                </div>
                <div className="order-1 md:order-2">
                  <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-6 shadow-lg">
                    <img 
                      src="/images/recipe-selection.png" 
                      alt="Selection de recettes Menioo depuis TikTok et Instagram" 
                      className="rounded-xl w-full"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-6 shadow-lg">
                    <img 
                      src="/images/planning.png" 
                      alt="Planning de repas intelligent Menioo" 
                      className="rounded-xl w-full"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center mb-5">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-menioo-orange to-menioo-orange-dark flex items-center justify-center text-white text-2xl font-bold mr-4 shadow-lg">
                      2
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-menioo-navy">Planifiez en 2 minutes</h3>
                      <p className="text-sm text-menioo-orange font-medium">Glissez-déposez, c'est réglé</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Organisez vos repas de la semaine en un instant. Glissez-déposez vos recettes sur le planning, et Menioo s'occupe du reste. Vos contraintes, votre rythme, vos envies — tout est pris en compte.
                  </p>
                  <div className="bg-menioo-cream/50 rounded-xl p-4 border border-menioo-green/10">
                    <p className="text-sm text-menioo-navy">
                      <span className="flex items-center mb-1">
                        <Sparkles className="h-4 w-4 text-menioo-green mr-2" />
                        <strong>Batch cooking :</strong>
                      </span>
                      <span className="ml-6 text-gray-600">Préparez plusieurs repas à l'avance le dimanche, Menioo optimise votre planning</span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center mb-5">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-menioo-green to-menioo-green-dark flex items-center justify-center text-white text-2xl font-bold mr-4 shadow-lg">
                      3
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-menioo-navy">Generez la liste de courses</h3>
                      <p className="text-sm text-menioo-green font-medium">Plus aucun oubli, zero gaspillage</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Menioo calcule tout : quantités exactes, ingrédients regroupés par rayons, pas de doublon. Vous partez en courses l'esprit léger, avec exactement ce dont vous avez besoin.
                  </p>
                  <div className="bg-menioo-cream/50 rounded-xl p-4 border border-menioo-green/10">
                    <p className="text-sm text-menioo-navy flex items-start gap-2">
                      <Timer className="h-4 w-4 text-menioo-orange shrink-0 mt-0.5" />
                      <span><strong>Bientôt :</strong> Connexion directe avec le drive de votre supermarché</span>
                    </p>
                  </div>
                </div>
                <div className="order-1 md:order-2">
                  <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-6 shadow-lg">
                    <img 
                      src="/images/grocery-list.png" 
                      alt="Liste de courses optimisée Menioo" 
                      className="rounded-xl w-full"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-6 shadow-lg overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80" 
                      alt="Cuisiner sans stress avec Menioo" 
                      className="rounded-xl w-full"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center mb-5">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-menioo-orange to-menioo-orange-dark flex items-center justify-center text-white text-2xl font-bold mr-4 shadow-lg">
                      4
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-menioo-navy">Cuisinez et savourez</h3>
                      <p className="text-sm text-menioo-orange font-medium">La fierte du fait-maison</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Tous les ingrédients sont là, la recette est devant vous. Plus qu'à se mettre aux fourneaux ! Savourez la fierté de cuisiner un plat découvert sur les réseaux — et de le réussir du premier coup.
                  </p>
                  <div className="bg-menioo-cream/50 rounded-xl p-4 border border-menioo-green/10">
                    <p className="text-sm text-menioo-navy flex items-center">
                      <ChefHat className="h-4 w-4 text-menioo-green mr-2" />
                      <strong>Bientot :</strong>&nbsp;Mode pas-a-pas avec timers integres
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          5. MID-PAGE CTA
          Conversion apres la demonstration de valeur
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-12 bg-gradient-to-r from-menioo-green to-menioo-green-dark">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-4">
            Pret à transformer votre façon de cuisiner ?
          </h2>
          <p className="text-lg text-white/80 mb-6 max-w-xl mx-auto">
            Rejoignez une communaute de passionnes qui cuisinent vraiment.
          </p>
          {ctaButton(
            'Commencer gratuitement — c\'est parti !',
            'inline-flex items-center bg-white text-menioo-green px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl'
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          6. FONCTIONNALITES DETAILLEES
          Features qui supportent le positionnement
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-menioo-cream/30">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Tout ce qu'il faut pour réussir en cuisine
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Des outils puissants, une interface simple. Menioo s'adapte à votre vie, pas l'inverse.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-all border border-gray-100 group">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <Bookmark className="h-5 w-5 text-menioo-green" />
                </div>
                <h3 className="font-bold text-menioo-navy">Import de recettes</h3>
              </div>
              <p className="text-gray-600 text-sm">Explorez la communauté, importez depuis TikTok, Instagram, blogs ou créez les vôtres. Tout au même endroit.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-all border border-gray-100 group">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-menioo-orange/10 flex items-center justify-center mr-3 group-hover:bg-menioo-orange/20 transition-colors">
                  <Calendar className="h-5 w-5 text-menioo-orange" />
                </div>
                <h3 className="font-bold text-menioo-navy">Planning intelligent</h3>
              </div>
              <p className="text-gray-600 text-sm">Glisser-déposer intuitif, gestion des restes, batch cooking. Votre semaine organisée sans effort.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-all border border-gray-100 group">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <ShoppingCart className="h-5 w-5 text-menioo-green" />
                </div>
                <h3 className="font-bold text-menioo-navy">Liste de courses optimisée</h3>
              </div>
              <p className="text-gray-600 text-sm">Triée par rayons, quantités calculées, anti-doublon. Courses rapides et sans oubli.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-all border border-gray-100 group">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-menioo-orange/10 flex items-center justify-center mr-3 group-hover:bg-menioo-orange/20 transition-colors">
                  <Target className="h-5 w-5 text-menioo-orange" />
                </div>
                <h3 className="font-bold text-menioo-navy">Manger sainement</h3>
              </div>
              <p className="text-gray-600 text-sm">Définissez vos objectifs (veggie, portions, équilibre...) et Menioo vous aide à manger sainement, naturellement.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-all border border-gray-100 group">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <Users className="h-5 w-5 text-menioo-green" />
                </div>
                <h3 className="font-bold text-menioo-navy">Partage & collaboration</h3>
              </div>
              <p className="text-gray-600 text-sm">En famille ou en coloc : partagez menus et listes, répartissez les courses et la cuisine.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 hover:shadow-lg transition-all border border-gray-100 group">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-xl bg-menioo-orange/10 flex items-center justify-center mr-3 group-hover:bg-menioo-orange/20 transition-colors">
                  <DollarSign className="h-5 w-5 text-menioo-orange" />
                </div>
                <h3 className="font-bold text-menioo-navy">Mode economie</h3>
              </div>
              <p className="text-gray-600 text-sm">Estimez le cout de vos repas, trouvez des alternatives moins cheres, maitrisez votre budget.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          7. PREUVES SOCIALES
          Temoignages et metriques de confiance
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              La communaute en parle
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez comment Menioo a transformé leur quotidien en cuisine.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            <div className="text-center">
              <div className="text-5xl font-bold text-menioo-green mb-2">+42€</div>
              <p className="text-gray-600 font-medium">économisés par semaine</p>
              <p className="text-sm text-gray-400">en moyenne par foyer</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-menioo-orange mb-2">2h</div>
              <p className="text-gray-600 font-medium">gagnées par semaine</p>
              <p className="text-sm text-gray-400">sur l'organisation des repas</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-menioo-green mb-2">4x</div>
              <p className="text-gray-600 font-medium">plus de recettes cuisinées</p>
              <p className="text-sm text-gray-400">vs. liker sans cuisiner</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center mb-4">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-menioo-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "Avec les recettes de la communaute et mes trouvailles TikTok, je cuisine 4 soirs sur 5 maintenant. J'ai divise mes Uber Eats par 3 !"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-menioo-green/20 flex items-center justify-center mr-3">
                  <span className="font-bold text-menioo-green">M</span>
                </div>
                <div>
                  <p className="font-semibold text-menioo-navy">Marie, 27 ans</p>
                  <p className="text-sm text-gray-500">Cuisiniere debutante</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center mb-4">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-menioo-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "En 3 semaines, j'ai cuisine plus de recettes que les 6 derniers mois. Mes enfants adorent participer au planning !"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-menioo-orange/20 flex items-center justify-center mr-3">
                  <span className="font-bold text-menioo-orange">T</span>
                </div>
                <div>
                  <p className="font-semibold text-menioo-navy">Thomas, 41 ans</p>
                  <p className="text-sm text-gray-500">Papa de 3 enfants</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-menioo-cream to-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center mb-4">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="h-5 w-5 text-menioo-orange fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic leading-relaxed">
                "Je partage mes recettes dans la communauté et je découvre celles des autres. Le batch cooking est devenu un jeu — mes objectifs veggie sont enfin tenus."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-menioo-green/20 flex items-center justify-center mr-3">
                  <span className="font-bold text-menioo-green">L</span>
                </div>
                <div>
                  <p className="font-semibold text-menioo-navy">Laura, 32 ans</p>
                  <p className="text-sm text-gray-500">Veggie & sportive</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-center">
            <div className="flex items-center bg-menioo-cream rounded-full px-6 py-3">
              <Shield className="h-6 w-6 text-menioo-green mr-3" />
              <span className="text-sm font-medium text-menioo-navy">
                Connexion Google sécurisée &bull; Vos données restent privées &bull; RGPD
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          8. COMPARATIF
          Menioo vs le statu quo
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-menioo-cream/30">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Menioo vs votre routine actuelle
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Les chiffres ne mentent pas. Voici ce que ça change concretement.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-2xl shadow-sm overflow-hidden">
                <thead>
                  <tr className="bg-menioo-navy text-white">
                    <th className="px-6 py-5 text-left font-semibold">Critere</th>
                    <th className="px-6 py-5 text-center font-semibold">
                      <span className="inline-flex items-center">
                        <ChefHat className="h-5 w-5 mr-2" />
                        Menioo
                      </span>
                    </th>
                    <th className="px-6 py-5 text-center font-semibold text-gray-300">Resto / Livraison</th>
                    <th className="px-6 py-5 text-center font-semibold text-gray-300">Impro sans plan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100">
                    <td className="px-6 py-5 font-medium text-menioo-navy">Cout / semaine</td>
                    <td className="px-6 py-5 text-center">
                      <Check className="h-5 w-5 mx-auto text-menioo-green" />
                      <div className="text-sm font-semibold text-menioo-green mt-1">~80€</div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <X className="h-5 w-5 mx-auto text-red-400" />
                      <div className="text-sm text-red-500 mt-1">~200€</div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <X className="h-5 w-5 mx-auto text-orange-400" />
                      <div className="text-sm text-orange-500 mt-1">~150€</div>
                    </td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50/50">
                    <td className="px-6 py-5 font-medium text-menioo-navy">Temps d'organisation</td>
                    <td className="px-6 py-5 text-center">
                      <Check className="h-5 w-5 mx-auto text-menioo-green" />
                      <div className="text-sm font-semibold text-menioo-green mt-1">2 min/jour</div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <X className="h-5 w-5 mx-auto text-red-400" />
                      <div className="text-sm text-red-500 mt-1">Attente</div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <X className="h-5 w-5 mx-auto text-orange-400" />
                      <div className="text-sm text-orange-500 mt-1">Stress quotidien</div>
                    </td>
                  </tr>
                  <tr className="border-t border-gray-100">
                    <td className="px-6 py-5 font-medium text-menioo-navy">Qualite nutritionnelle</td>
                    <td className="px-6 py-5 text-center"><Check className="h-5 w-5 mx-auto text-menioo-green" /></td>
                    <td className="px-6 py-5 text-center"><X className="h-5 w-5 mx-auto text-red-400" /></td>
                    <td className="px-6 py-5 text-center"><X className="h-5 w-5 mx-auto text-orange-400" /></td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50/50">
                    <td className="px-6 py-5 font-medium text-menioo-navy">Respect des objectifs</td>
                    <td className="px-6 py-5 text-center"><Check className="h-5 w-5 mx-auto text-menioo-green" /></td>
                    <td className="px-6 py-5 text-center"><X className="h-5 w-5 mx-auto text-red-400" /></td>
                    <td className="px-6 py-5 text-center"><X className="h-5 w-5 mx-auto text-red-400" /></td>
                  </tr>
                  <tr className="border-t border-gray-100">
                    <td className="px-6 py-5 font-medium text-menioo-navy">Fierte personnelle</td>
                    <td className="px-6 py-5 text-center"><Check className="h-5 w-5 mx-auto text-menioo-green" /></td>
                    <td className="px-6 py-5 text-center"><X className="h-5 w-5 mx-auto text-red-400" /></td>
                    <td className="px-6 py-5 text-center"><X className="h-5 w-5 mx-auto text-orange-400" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          9. FAQ
          Repondre aux objections
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Vos questions, nos reponses
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              {
                question: "C'est vraiment gratuit ?",
                answer: "Oui ! La version gratuite est complete pour demarrer : creation de recettes, planification sur 1 semaine, liste de courses. Des options Premium arriveront bientot pour les fonctionnalites avancees, mais le gratuit restera toujours disponible."
              },
              {
                question: "Ou trouver des recettes sur Menioo ?",
                answer: "Vous pouvez explorer les milliers de recettes partagées par la communauté Menioo, créer les vôtres ou bientôt importer automatiquement celles de vos créateurs préférés depuis TikTok, Instagram et les blogs culinaires (fonctionnalité Premium à venir). La communauté est au cœur de Menioo !"
              },
              {
                question: "L'application mobile est disponible ?",
                answer: "Pas encore ! Menioo est accessible sur navigateur web (ordinateur, tablette, mobile). Les applications natives iOS et Android sont en cours de développement et arrivent bientôt. Inscrivez-vous pour être prévenu du lancement."
              },
              {
                question: "Ca convient aux familles ?",
                answer: "Absolument. Partage de menus et de listes de courses, planification pour tout le foyer. Chacun peut contribuer selon ses envies et son niveau."
              },
              {
                question: "Et les regimes speciaux ?",
                answer: "Veggie, vegan, sans gluten, paléo, moins de viande... Définissez vos préférences et objectifs, Menioo vous aide à les respecter naturellement, semaine après semaine."
              },
              {
                question: "Je suis nul en cuisine, c'est pour moi ?",
                answer: "Surtout pour vous ! Menioo vous guide pas a pas. Commencez par des recettes simples de 15-20 minutes, progressez à votre rythme. Vous serez surpris de ce que vous pouvez cuisiner."
              },
              {
                question: "Mes donnees personnelles sont en securite ?",
                answer: "Totalement. Connexion sécurisée via Google, données chiffrées, jamais vendues ni partagées. Vous gardez le contrôle total. Suppression possible à tout moment. Conforme RGPD."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-menioo-cream/50 rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-menioo-cream/80 transition-colors"
                >
                  <span className="font-semibold text-menioo-navy pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <Minus className="h-5 w-5 text-menioo-green flex-shrink-0" />
                  ) : (
                    <Plus className="h-5 w-5 text-menioo-green flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          10. ROADMAP — Ce qui arrive bientot
          Garder l'excitation avec les features futures
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-menioo-cream to-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-menioo-navy mb-4">
              Ce n'est que le debut
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Menioo evolue en permanence. Voici ce qui arrive prochainement — et ce n'est qu'un apercu.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <Smartphone className="h-6 w-6 text-menioo-green" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Applications mobiles</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Apps natives iOS et Android pour cuisiner partout, meme hors connexion. Scannez, planifiez, cuisinez — dans votre poche.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-orange/10 flex items-center justify-center mr-3 group-hover:bg-menioo-orange/20 transition-colors">
                  <Zap className="h-6 w-6 text-menioo-orange" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Assistant IA</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Intelligence artificielle pour adapter les recettes à vos goûts, suggérer des menus et vous coacher en cuisine.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <ChefHat className="h-6 w-6 text-menioo-green" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Mode pas-a-pas</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Instructions détaillées avec timers intégrés et conseils pour ne jamais rater vos plats.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-orange/10 flex items-center justify-center mr-3 group-hover:bg-menioo-orange/20 transition-colors">
                  <Globe className="h-6 w-6 text-menioo-orange" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Lecteur video integre</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Regardez les videos de recettes TikTok directement dans l'app pendant que vous cuisinez.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <Play className="h-6 w-6 text-menioo-green" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Import TikTok/Instagram</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Collez le lien d'une vidéo et Menioo extrait automatiquement la recette, les ingrédients et les quantités.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-orange/10 flex items-center justify-center mr-3 group-hover:bg-menioo-orange/20 transition-colors">
                  <ShoppingCart className="h-6 w-6 text-menioo-orange" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Drive & Click and Collect</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Connexion directe avec les grandes enseignes pour commander vos courses en 1 clic.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <Target className="h-6 w-6 text-menioo-green" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Ingredients specifiques</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Marques, produits bio, labels... choisissez exactement ce que vous voulez dans votre liste.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-orange/10 flex items-center justify-center mr-3 group-hover:bg-menioo-orange/20 transition-colors">
                  <Star className="h-6 w-6 text-menioo-orange" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Suggestions intelligentes</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Recettes adaptées à vos objectifs, saison, budget et habitudes culinaires.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-transparent hover:border-menioo-green/20 transition-all hover:shadow-md group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-2xl bg-menioo-green/10 flex items-center justify-center mr-3 group-hover:bg-menioo-green/20 transition-colors">
                  <TrendingUp className="h-6 w-6 text-menioo-green" />
                </div>
                <div>
                  <h3 className="font-bold text-menioo-navy">Suivi des objectifs</h3>
                  <span className="text-xs bg-menioo-orange/10 text-menioo-orange px-2.5 py-1 rounded-full font-semibold">Bientot</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Tableaux de bord detailles : calories, budget, consommation de viande, legumes et plus.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-sm border border-gray-100">
              <Sparkles className="h-5 w-5 text-menioo-green mr-2" />
              <span className="text-menioo-navy font-medium">
                Des idees ? <strong>Rejoignez la communaute</strong> et influencez le futur de Menioo
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          11. CTA FINAL
          Derniere chance de convertir
      ═══════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-menioo-navy text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-menioo-green/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-menioo-orange/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="container mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-6">
            Ce soir, vous cuisinez{' '}
            <span className="text-menioo-orange">un vrai repas fait maison</span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Gratuit. Sans carte bancaire. Pret en 2 minutes.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {ctaButton(
              'Commencer gratuitement',
              'inline-flex items-center justify-center bg-menioo-orange text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-menioo-orange-dark transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5'
            )}
            <Link 
              to="/pricing"
              className="inline-flex items-center justify-center bg-transparent border-2 border-gray-500 text-gray-300 px-10 py-5 rounded-xl font-semibold text-xl hover:border-white hover:text-white transition-all"
            >
              Voir les tarifs
            </Link>
          </div>
          
          <p className="mt-6 text-gray-400 flex items-center justify-center">
            <Shield className="h-4 w-4 mr-2" />
            Inscription sécurisée &bull; Aucune carte requise &bull; Annulable à tout moment
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          12. FOOTER
      ═══════════════════════════════════════════════════════════ */}
      <footer className="bg-menioo-navy text-white py-12 border-t border-gray-700">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-1">
              <div className="flex items-center mb-4">
                <ChefHat className="h-8 w-8 text-menioo-green mr-2" />
                <span className="text-2xl font-display font-bold">Menioo</span>
              </div>
              <p className="text-gray-400 mb-4">
                De l'inspiration à l'assiette. Recettes de la communauté, de vos créateurs préférés ou les vôtres — cuisinez pour de vrai.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Produit</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" onClick={handleAppRedirect} className="hover:text-white transition-colors">Planning de repas</a></li>
                <li><a href="#" onClick={handleAppRedirect} className="hover:text-white transition-colors">Liste de courses</a></li>
                <li><a href="#" onClick={handleAppRedirect} className="hover:text-white transition-colors">Recettes</a></li>
                <li><Link to="/why-menioo" className="hover:text-white transition-colors">Pourquoi Menioo</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Tarifs</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="mailto:contact@menioo.app" className="hover:text-white transition-colors">Contact</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-lg mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/terms" className="hover:text-white transition-colors">CGU</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Confidentialite</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">&copy; {new Date().getFullYear()} Menioo. Tous droits reserves.</p>
            <p className="text-gray-400 mt-4 md:mt-0">
              Fait avec <Heart className="h-4 w-4 text-menioo-orange inline mx-1" /> par l'equipe Menioo
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
